/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Platform } from '@ionic/angular';
import { environment } from '@env/environment';
import { API } from '@app/core/models';
import { CustomEncoder } from './http.util';

@Injectable()
export class CoreDataService {
  constructor(private _platform: Platform, private _httpClient: HttpClient) {}

  getDomain() {
    return environment.app.domain;
  }

  getBaseUrlAndApi() {
    return environment.app.baseUrl + environment.app.api;
  }

  getHttpOptions(params?: Record<string, any>): API.HttpOptions {
    const options: API.HttpOptions = this._platform.is('capacitor')
      ? {}
      : {
          headers: new HttpHeaders({
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Authorization: '',
          }),
          withCredentials: true,
        };
    if (params) {
      options.params = this.getHttpParams(params);
    }
    return options;
  }

  get<T>(url: string, options?: API.HttpOptions): Observable<T> {
    const fullUrlPath = this._formatUrlPath(url);
    const defaultOptions = this.getHttpOptions();
    return this.getDirect(fullUrlPath, options || defaultOptions);
  }

  getDirect<T>(url: string, options?: API.HttpOptions): Observable<T> {
    return this._httpClient.get<T>(url, {
      ...options,
      params: options?.params || undefined,
    });
  }

  post<T>(url: string, body?: any, options?: API.HttpOptions): Observable<T> {
    const fullUrlPath = this._formatUrlPath(url);
    return this.postDirect(fullUrlPath, body, options);
  }

  postDirect<T>(
    url: string,
    body?: any,
    options?: API.HttpOptions,
  ): Observable<T> {
    return this._httpClient.post<T>(url, body, {
      ...options,
      params: options?.params || undefined,
    });
  }

  put<T>(
    url: string,
    body?: any,
    options?: Record<string, any>,
  ): Observable<T> {
    const fullUrlPath = this._formatUrlPath(url);
    return this._httpClient.put<T>(fullUrlPath, body, options);
  }

  delete<T>(url: string, options?: API.HttpOptions): Observable<T> {
    const fullUrlPath = this._formatUrlPath(url);
    return this._httpClient.delete<T>(fullUrlPath, {
      ...options,
      params: options?.params || undefined,
    });
  }

  patch<T>(url: string, body?: any, options?: API.HttpOptions): Observable<T> {
    const fullUrlPath = this._formatUrlPath(url);
    return this._httpClient.patch<T>(fullUrlPath, body, {
      ...options,
      params: options?.params || undefined,
    });
  }

  generatePlatformQueryParameters(
    httpParamsAPI: HttpParams,
    queryParamsObjectList: Record<string, any>,
  ): any {
    let queryParams = queryParamsObjectList;

    if (!this._platform.is('capacitor')) {
      queryParams = setMultiple(httpParamsAPI, queryParamsObjectList);
    }
    function setMultiple(api: HttpParams, values: Record<string, any>) {
      for (const key in values) {
        if (values.hasOwnProperty(key)) {
          api = api.set(key, values[key]);
        }
      }
      return api;
    }
    return queryParams;
  }

  getHttpParams(
    queryParamsObjectList: Record<string, any>,
  ): HttpParams | { [param: string]: string | string[] } {
    const httpParamsAPI = new HttpParams({ encoder: new CustomEncoder() });
    Object.entries(queryParamsObjectList).forEach(([key, value]) => {
      queryParamsObjectList[key] =
        typeof value === 'object' && value !== null
          ? JSON.stringify(value)
          : String(value);
    });
    const httpParams = this.generatePlatformQueryParameters(
      httpParamsAPI,
      queryParamsObjectList,
    );
    return httpParams;
  }

  private _formatUrlPath(url: string): string {
    return environment.app.baseUrl + environment.app.api + url;
  }
}
